import React from 'react';
import { FormattedDate, FormattedNumber, FormattedRelativeTime } from 'react-intl';
import { Link } from 'react-router-dom';
import { Tag, Tooltip } from 'antd';
import { CheckOutlined, StopOutlined } from '@ant-design/icons';
import byteSize from 'byte-size';

import { SUPER_USER } from 'shared/constants/permissions';
import { dayjs } from 'shared/utils/date';
import selectUnit from 'shared/utils/selectUnit';
import { durationFormat } from 'shared/utils/formatter';

import HasPermission from 'components/HasPermission';
import MiddleEllipsis from 'components/MiddleEllipsis';

export const renderCustomer = ({ id, name }) => (
  <HasPermission permission={SUPER_USER} fallback={name}>
    <Link to={`/administration/customers/${id}`}>{name}</Link>
  </HasPermission>
);

export const renderNumber = value => (
  value != null ? <FormattedNumber value={value} /> : null
);

export const renderPercent = value => (
  value != null ? <FormattedNumber value={value} style="percent" maximumFractionDigits={2} /> : null
);

export const renderBool = value => {
  if (value === true || value === 'true' || value === 1 || value === '1') {
    return <CheckOutlined />;
  }
  if (value === false || value === 'false' || value === 0 || value === '0') {
    return <StopOutlined />;
  }
  return null;
};

export const renderTruthy = value => {
  if (value) {
    return <CheckOutlined />;
  }
  return null;
};

export const renderRelativeDate = date => {
  if (date) {
    const d = dayjs(date);

    if (d.isValid()) {
      return (
        <Tooltip title={<FormattedDate value={d.toDate()} year="numeric" day="numeric" month="short" hour="numeric" minute="numeric" />}>
          <span><FormattedRelativeTime {...selectUnit(d.toDate())} /></span>
        </Tooltip>
      );
    }
  }
  return null;
};

export const renderDateTime = date => date && <FormattedDate value={dayjs.utc(date)} year="numeric" day="numeric" month="short" hour="numeric" minute="numeric" />;
export const renderDate = date => date && <FormattedDate value={dayjs.utc(date)} year="numeric" day="numeric" month="short" />;
export const renderTime = date => date && <FormattedDate value={dayjs.utc(date)} hour="numeric" minute="numeric" second="numeric" />;

export const renderTags = arr => arr?.map(c => <Tag key={c.id}>{c.title}</Tag>);
export const renderLink = url => {
  if (!url) {
    return null;
  }

  return (
    <Tooltip title={url}>
      <a href={url} target="_blank" rel="noopener noreferrer">
        <MiddleEllipsis width={300}>
          <span className="constrainedChild">
            <span className="ellipseMe">{url}</span>
          </span>
        </MiddleEllipsis>
      </a>
    </Tooltip>
  );
};

export const renderVersion = (version, record) => {
  let v;
  if (record.versions?.length > 1) {
    v = 'Multiple';
  }

  if (record.versions?.length === 1) {
    ([v] = record.versions);
  }

  if (version) {
    v = version;
  }

  if (!v) {
    return null;
  }

  return (
    <Tag>{v}</Tag>
  );
};

export function getRenderer(type) {
  switch (type) {
    case 'number':
      return renderNumber;
    case 'percent':
      return renderPercent;
    case 'bool':
    case 'boolean':
      return renderBool;
    case 'date':
      return renderDate;
    case 'time':
      return renderTime;
    case 'datetime':
      return renderDateTime;
    case 'duration':
    case 'milliseconds':
      return durationFormat;
    case 'seconds':
      return seconds => durationFormat(seconds * 1000);
    case 'relativeDate':
      return renderRelativeDate;
    case 'tags':
      return renderTags;
    case 'url':
      return renderLink;
    case 'bytes':
      return v => byteSize(v)?.toString();
    default:
      return v => v?.toString();
  }
}
